import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  useTheme,
  Box,
  Icon,
  Spinner,
  ISelectMiniOption,
  IconButton,
  Tooltip,
  Gap,
  Toggle,
  Input,
  Label,
  TooltipComponent,
  SvgIcon,
} from '@salecycle/ui-component-library';
import { useFeatureFlags } from 'core/app/AppContext';
import { Configs } from '../../../configs';
import { SegmentDropdown } from '../segment-item/SegmentDropdown';
import { SegmentName } from '../segment-item/SegmentName';
import { SortableHandle } from 'react-sortable-hoc';
import ProgressBar from '../segment-item/ProgressBar';
import { getProgress, Progress, splitTestNameValidation } from '../../progress';
import { getStatusColor } from '../../../utils/helpers';
import { State as CampaignState } from 'entities/lifecycle/lifeCycle';
import {
  useDoesSplitTestExist,
  useOffsiteCampaignEditorDispatch,
  useOffsiteCampaignEditorState,
} from '../../../context/UseOffsiteCampaignEditorContext';
import { SegmentConfig } from '../../../configs/segmentConfigs';
import {
  ICycle,
  IPercentageSplit,
  ISegment,
  ISegmentFilter,
  IVisitorMessageType,
} from '../../../../entities/offsiteCampaign';
import { UnsubscribeSettings } from 'sections/client-management/entities';
import { getCycleConfig } from '../../../configs/newCycles';
import { SegmentFilterList } from '../segment-item/SegmentFilterList';
import { getSegmentFilterConfig } from '../../../configs/newSegmentFilters';
import { SplitTestList } from '../../action-steps/SplitTestList';
import ConfirmationModal from 'components/experimental/ConfirmationModal';
import { IconLabel } from 'components/experimental/IconLabel';
import { SegmentationModal, SegmentationRule } from '@salecycle/experimental/Modal/SegmentationModal';
import { FilterOptionsFactory } from '../segment-item/segment-filter-items/FilterOptionsFactory';
import { useMemo } from 'react';
import { TestCampaignOptions } from './components/TestCampaignOptions';

const ALLOWED_MESSAGE_TYPES: readonly IVisitorMessageType[] = ['email', 'sms', 'whatsapp', 'rcs'];

const StyledSegmentListItem = styled.div<{ campaignState?: CampaignState }>`
  box-shadow: ${({ theme }) => theme.vars.boxShadow.overlayMenu};
  border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
  background-color: ${({ theme }) => theme.vars.newColor.lightGS2};
  min-width: max-content;
  position: relative;
  border-top: ${({ campaignState, theme }) =>
    campaignState ? `0.5rem solid ${getStatusColor(campaignState, theme)}` : null};
`;

const StyledHeader = styled.div`
  margin-bottom: 2rem;
  ​ button {
    border-color: transparent;
    background-color: transparent;
  }
  ​ .priority {
    width: 3.5rem;
    height: 3.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid ${({ theme }) => theme.vars.newColor.darkGS2};
    border-radius: 100%;
    background-color: ${({ theme }) => theme.vars.newColor.white};
  }
  .controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    .group {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      align-items: center;
      ​ div:nth-child(2) {
        height: 100%;
        ​ button {
          width: 100%;
          height: 100%;
          align-items: center;
        }
      }
    }
  }
  .meta {
    p {
      margin: 0;
    }
  }
`;

const StyledDragHandle = styled.div`
  display: flex;
  justify-content: center;
  cursor: grab;
`;

const StyledProgressMessages = styled.div`
  background-color: ${({ theme }) => theme.vars.newColor.darkGS1};
  padding: 2rem;
  color: #fff;
  ​ ul {
    padding-left: 2rem;
    li {
      margin-bottom: 1rem;
    }
  }
`;

const StyledSegmentDiv = styled.div`
  display: 'flex';
  margin-top: '10px';
  margin-bottom: 0;
  flex-direction: 'row';
  gap: '20px';
`;
const StyledVerticalSpacing = styled.div`
  padding-top: 2rem;
`;

const StyledSplitTestIdDivItem = styled.div`
  flex: 0 0 50%;
  padding-left: 20px;
`;

const StyledMarginLeft = styled.div`
  padding-top: 5px;
  margin-left: 10px;
`;

interface IProps {
  segment: ISegment;
  index: number;
  campaignState: CampaignState;
}

export type SegmentListItemProps = IProps;

export const SegmentListItem: React.FC<IProps> = (props): JSX.Element => {
  const dispatch = useOffsiteCampaignEditorDispatch();
  const { t } = useTranslation('campaigns');
  const theme = useTheme();
  const { campaign, site, activeChannels } = useOffsiteCampaignEditorState();
  const [sessionTime] = React.useState<number>(site?.sessionTime | 0);
  const [unsubscribeSettings] = React.useState<UnsubscribeSettings>(site?.unsubscribeSettings);
  const [segment, setSegment] = React.useState<ISegment>();
  const [progress, setProgress] = React.useState<Progress>();
  const [config, setConfig] = React.useState<SegmentConfig>();
  const [splits, setSplits] = React.useState<IPercentageSplit[]>();
  const [newCycleOptions, setNewCycleOptions] = React.useState<ISelectMiniOption[]>();
  const [showModal, setShowModal] = React.useState<boolean>(false);
  const [showFilterModal, setShowFilterModal] = React.useState<boolean>(false);
  const { flagEnabled } = useFeatureFlags();
  const featureSplitTest = flagEnabled('ld-split-test-scheduling');
  const featureControlGroup = flagEnabled('ld-control-group');
  const doesSplitTestExist = useDoesSplitTestExist();

  const filterFactory = useMemo(() => new FilterOptionsFactory(t, campaign.type === 'browseAbandonment'), [
    t,
    campaign.type,
  ]);
  const closeModal = () => setShowModal(false);
  const closeFilterModal = () => setShowFilterModal(false);

  const [splitTestName, setSplitTestName] = React.useState<string>('');
  // Set Initial Segment
  React.useEffect(() => {
    const selectedSegment: ISegment = campaign?.segments?.find(x => x.id === props.segment.id);

    if (selectedSegment) {
      setSegment(selectedSegment);
      featureSplitTest && setSplitTestName(segment?.splitTestName);
      setProgress(
        getProgress(
          selectedSegment,
          sessionTime,
          site.unsubscribeSettings || unsubscribeSettings,
          campaign.type,
          t,
          featureSplitTest,
          featureControlGroup,
        ),
      );
      setConfig(Configs.getSegmentConfig(campaign.type, activeChannels, t));

      const splitsWithSortedCycles = selectedSegment.percentageSplits.map(
        s =>
          ({
            id: s.id,
            name: s.name,
            percentage: s.percentage,
            cycles: sortCycles(s.cycles),
          } as IPercentageSplit),
      );
      setSplits(splitsWithSortedCycles);
    }
  }, [
    props.segment.id,
    campaign.segments,
    campaign.type,
    sessionTime,
    unsubscribeSettings,
    campaign,
    t,
    setSplitTestName,
    segment?.splitTestName,
    featureSplitTest,
    featureControlGroup,
    site.unsubscribeSettings,
    activeChannels,
  ]);

  React.useEffect(() => {
    if (config) {
      setNewCycleOptions(
        config.availableChannelTypes.reduce<ISelectMiniOption[]>((acc, channelType) => {
          const cycleConfig = getCycleConfig(channelType, t);
          if (cycleConfig) {
            acc.push({ value: cycleConfig.defaultValue.messageType, label: cycleConfig.heading });
          }
          return acc;
        }, []),
      );
    }
  }, [config, segment, t]);

  function sortCycles(rawCycles: ICycle[]): ICycle[] {
    if (rawCycles.length <= 1) {
      return rawCycles;
    }
    const emailCycles: ICycle[] = [];
    // TODO remove block if we can safely remove 'browse' from message types
    rawCycles.forEach(cycle => {
      if (ALLOWED_MESSAGE_TYPES.includes(cycle.messageType)) {
        emailCycles.push(cycle);
      }
    });
    emailCycles.forEach(cycle => {
      if (cycle.id === undefined) {
        return rawCycles;
      }
    });
    const sortedEmailCycles: Array<ICycle> = emailCycles.sort((n1, n2) => {
      if (n1.delay > n2.delay) {
        return 1;
      }
      if (n1.delay <= n2.delay) {
        return -1;
      }
      return 0;
    });

    const sortedCycles: ICycle[] = [];
    sortedEmailCycles.forEach(c => {
      sortedCycles.push(c);
    });
    return sortedCycles;
  }

  const segmentPriority = props.index + 1;

  const onChangeSplitTestName = (value?: string, e?: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: 'UPDATE_SPLIT_TEST_NAME',
      payload: {
        segmentId: segment?.id,
        name: value,
      },
    });
    setSplitTestName(value);
  };

  const DragHandle = SortableHandle(() => (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <StyledDragHandle tabIndex={0} data-testid="DragHandle">
      <Icon icon="grip-horizontal" color={theme.vars.newColor.darkGS2} />
    </StyledDragHandle>
  ));

  const onAddSegmentFilterSelect = React.useCallback(
    (filterType: string, channel?: string): void => {
      if (filterType === 'custom') {
        if (segment?.segmentFilters?.filter(segment => segment.type === 'custom').length < 1) {
          setShowFilterModal(true);
        } else {
          dispatch({
            type: 'UPDATE_SEGMENT_CUSTOM_FILTERS',
            payload: {
              segmentId: props.segment.id,
              filters: [],
            },
          });
        }
        return;
      }
      const newFilter = getSegmentFilterConfig(filterType, t, channel);
      dispatch({
        type: 'ADD_SEGMENT_FILTER',
        payload: {
          segmentFilter: newFilter.defaultValue,
          segmentId: props.segment.id,
        },
      });
    },
    [dispatch, props.segment.id, segment?.segmentFilters, t],
  );

  const onAddTestFilterClick = React.useCallback(
    (channel: string) => {
      onAddSegmentFilterSelect('testing', channel);
    },
    [onAddSegmentFilterSelect],
  );

  const applyFilters = (rules: SegmentationRule<any, any, any>[][]) => {
    const filters = mapRulesToFilters(rules);
    dispatch({
      type: 'UPDATE_SEGMENT_CUSTOM_FILTERS',
      payload: {
        segmentId: props.segment.id,
        filters,
      },
    });
  };

  const handleDeleteSegment = () => {
    dispatch({
      type: 'REMOVE_SEGMENT',
      payload: {
        segmentId: props.segment.id,
      },
    });
  };

  const mapFiltersToRules = React.useMemo((): SegmentationRule<any, any, any>[][] => {
    return (
      segment?.segmentFilters
        ?.filter(f => f.type === 'custom')
        .map(f => [
          {
            configuration: {
              condition: filterFactory.inputTypeSelector(f.options.join('.*.')) !== 'bool' ? f.comparison : '', // avoids rendering of the second dropdown in the modal
              value: f.value,
            },
            type: f.options.join('.*.'),
          },
        ]) ?? [[]]
    );
  }, [segment, filterFactory]);

  const mapRulesToFilters = (rules: SegmentationRule<any, any, any>[][]): ISegmentFilter[] => {
    return rules
      .filter(AND => AND.length > 0) // remove possible empties
      .map(AND => {
        // This ignores the OR sub-arrays, but automaticaly gets the first (and only) element
        const filter = getSegmentFilterConfig('custom', t).defaultValue;
        return {
          ...filter,
          value: AND[0].configuration.value,
          options: AND[0].type.split('.*.'),
          comparison: AND[0].configuration.condition || '=', // default value for boolean types
        } as ISegmentFilter;
      });
  };

  const iconLabelColor = theme.vars.newColor.darkGS2;

  if (!segment) {
    return (
      <StyledSegmentListItem data-testid="Segment" campaignState={props.campaignState}>
        <Spinner />
      </StyledSegmentListItem>
    );
  }

  const isNewSplitTestNameValid = featureSplitTest ? splitTestNameValidation(splitTestName, t).expression : undefined;

  return (
    <StyledSegmentListItem data-testid="Segment" campaignState={props.campaignState}>
      <Box padding="2rem" stackItems={true}>
        {progress.percent < 100 ? (
          <div data-testid="segment-progress">
            <IconLabel
              icon="arrow-down"
              color={iconLabelColor}
              text={t('campaigns:offsiteEditor.segments.guides.progressGuide')}
            ></IconLabel>
            <StyledVerticalSpacing>
              <ProgressBar percent={progress.percent} />
            </StyledVerticalSpacing>
          </div>
        ) : null}
        <StyledHeader data-testid="SegmentHeader" id={`segment-header-${props.index}`}>
          <div className="controls">
            <div className="priority">{segmentPriority}</div>
            <div className="group">
              <DragHandle />
              <Tooltip title={t('campaigns:offsiteEditor.segments.delete.iconAriaLabel')} position="top">
                <IconButton
                  ariaLabel={t('campaigns:offsiteEditor.segments.delete.iconAriaLabel')}
                  icon="trash"
                  onClick={() => setShowModal(true)}
                  data-testid="delete-segment-button"
                />
              </Tooltip>
              <SegmentDropdown segmentId={props.segment.id} segment={segment} />
            </div>
          </div>
          <IconLabel
            icon="arrow-down"
            color={iconLabelColor}
            text={t('campaigns:offsiteEditor.segments.guides.startSegment')}
          ></IconLabel>
          <div className="meta">
            <SegmentName segmentId={props.segment.id} name={segment.name} />
            <p>{config?.title}</p>
          </div>
          <StyledVerticalSpacing>
            <IconLabel
              icon="arrow-down"
              color={iconLabelColor}
              text={t('campaigns:offsiteEditor.segments.guides.whoIsAudience')}
            ></IconLabel>
          </StyledVerticalSpacing>
        </StyledHeader>
        <div data-testid="SegmentFilterSelector">
          <Toggle
            label={t(`campaigns:offsiteEditor.segments.filters.addNewFilter.label`)}
            checked={segment?.segmentFilters?.filter(segment => segment.type === 'custom').length <= 0}
            ariaLabel={t(`campaigns:offsiteEditor.segments.filters.addNewFilter.label`)}
            onChange={() => onAddSegmentFilterSelect('custom')}
            margin={false}
          />
        </div>
        <StyledVerticalSpacing data-testid="SegmentFilters">
          <SegmentFilterList
            campaignState={campaign.state}
            segmentFilters={segment?.segmentFilters || []}
            segmentId={segment.id}
            onCustomFilterClick={() => setShowFilterModal(true)}
          />
        </StyledVerticalSpacing>
        <Gap bottom={10} />
        <TestCampaignOptions segment={segment} onClick={onAddTestFilterClick} />
        {featureSplitTest && segment.splitTestId !== undefined && doesSplitTestExist && (
          <StyledSegmentDiv style={{ flexDirection: 'row', display: 'flex', gap: '20px' }}>
            <StyledSegmentDiv style={{ display: 'flex', gap: '5px' }}>
              <Input
                inputTestId="splitTestName1"
                spacingBelow={false}
                ariaLabel={`${t('campaigns:offsiteEditor.segments.modal.newSplitTestName')}`}
                label={`${t('campaigns:offsiteEditor.segments.splitTests.splitName.newAriaLabel')}`}
                value={splitTestName}
                disabled={campaign.state !== 'TESTING'}
                style={{ marginBottom: 0, maxHeight: '20px' }}
                onChange={value => onChangeSplitTestName(value)}
                validationMessage={
                  splitTestName && !isNewSplitTestNameValid && splitTestNameValidation(splitTestName, t).message
                }
                validationState={isNewSplitTestNameValid ? 'complete' : splitTestName ? 'warning' : null}
                tooltipComponent={
                  <>
                    <TooltipComponent label={t('campaigns:offsiteEditor.segments.splitTests.tooltip')} position="Right">
                      <SvgIcon
                        size="16"
                        name="circle-question"
                        id="circle-question"
                        color={theme.designToken.primary700}
                      ></SvgIcon>
                    </TooltipComponent>
                  </>
                }
              />
            </StyledSegmentDiv>
            {segment.splitTestId !== undefined && segment.splitTestId !== '' && doesSplitTestExist && (
              <StyledSplitTestIdDivItem>
                <Label
                  name={t('campaigns:offsiteEditor.segments.splitTests.splitTestId.label')}
                  data-testid="SplitTestIdLabel"
                  tooltipComponent={
                    <>
                      <TooltipComponent
                        label={t('campaigns:offsiteEditor.segments.splitTests.splitTestId.tooltip')}
                        position="Right"
                      >
                        <SvgIcon
                          size="16"
                          name="circle-question"
                          id="circle-question"
                          color={theme.designToken.primary700}
                        ></SvgIcon>
                      </TooltipComponent>
                    </>
                  }
                ></Label>
                <Gap bottom={10} />
                <StyledMarginLeft>
                  <Label data-testid="SplitTestIdData" name={segment.splitTestId} isBold={true}></Label>
                </StyledMarginLeft>
              </StyledSplitTestIdDivItem>
            )}
          </StyledSegmentDiv>
        )}
        <Gap bottom={10} />
        <SplitTestList
          splits={splits}
          segmentId={segment.id}
          campaignState={campaign.state}
          newCycleOptions={newCycleOptions}
          sessionTime={sessionTime}
          key={`${segment.splitTestId}+${campaign.splitTestStartDate}+${campaign.splitTestEndDate}`}
        />
        <IconLabel
          icon="arrow-right"
          color={iconLabelColor}
          text={t('campaigns:offsiteEditor.segments.guides.ifNoMatchingAudience')}
        ></IconLabel>
      </Box>
      {progress.messages.length > 0 && (
        <StyledProgressMessages>
          <h5>*Required</h5>
          <ul>
            {progress.messages.map((message: string, i: number) => (
              <li key={`progress-message-${i}`}>{message}</li>
            ))}
          </ul>
        </StyledProgressMessages>
      )}
      {showModal && (
        <ConfirmationModal
          cancelButtonLabel={t('campaigns:offsiteEditor.segments.deleteModal.cancel')}
          description={t('campaigns:offsiteEditor.segments.deleteModal.description', { segment: segment.name })}
          heading={t('campaigns:offsiteEditor.segments.deleteModal.heading', { segment: segment.name })}
          isOpen={showModal}
          okButtonLabel={t('campaigns:offsiteEditor.segments.deleteModal.confirm')}
          onCancelButtonClicked={closeModal}
          onCloseButtonClicked={closeModal}
          onOkButtonClicked={handleDeleteSegment}
        />
      )}
      {showFilterModal && (
        <SegmentationModal
          open={showFilterModal}
          onClose={closeFilterModal}
          onSave={applyFilters}
          initialRules={mapFiltersToRules}
          optionsFactory={filterFactory}
        />
      )}
    </StyledSegmentListItem>
  );
};
